import { WEB_BASE_URL } from '../api/client.ts';
import { me, orders, payments } from '../api/endpoints.ts';
import type { CardVerificationResult, PaymentConfig, PaymentConfirmation, SavedCard } from '../api/types.ts';
import { createStore } from '../core/store.ts';
import { currentLang } from '../i18n/index.ts';

/**
 * Оплата. Два пути по настройке сети (`cardEntry` в конфиге оплаты):
 * `inline` — карта вводится в приложении, форма шлёт номер напрямую эквайеру (наш сервер его не
 * видит), после 3-D Secure эквайер сохраняет карту, и следующие заказы платятся ею в одно касание;
 * `hosted` — браузер уходит на страницу эквайера. В обоих случаях эквайер возвращает браузер на
 * страницу подтверждения сервера, а оттуда кнопка ведёт обратно: `/?order=<id>&payment=1`.
 * Запуск с этими параметрами спрашивает результат.
 *
 * Ход оплаты показывает плашка вверху экрана (`paymentStatusStore`): «идёт оплата», «оплачено»,
 * «проблема с оплатой» с переходом в заказ. Она не блокирует экран.
 */

export const paymentConfigStore = createStore<PaymentConfig>({ provider: 'mock', hosted: true, cardEntry: 'hosted', savedCards: false, cardToken: false, applePay: false, testCard: false });

export const savedCardsStore = createStore<{ items: SavedCard[]; loaded: boolean }>({ items: [], loaded: false });

/** Способ оплаты на оформлении: сохранённая карта, новая карта, страница банка или тестовая карта мока. */
export type PaymentMethod = { kind: 'saved'; cardId: string } | { kind: 'new' } | { kind: 'bank' } | { kind: 'test' };

export type PaymentPhase = 'paying' | 'paid' | 'problem';
export type PaymentStatus = { id: number; orderId: string; phase: PaymentPhase };

export const paymentStatusStore = createStore<PaymentStatus | null>(null);

let statusSeq = 0;
let hideTimer: ReturnType<typeof setTimeout> | null = null;

/** Плашка «идёт оплата» висит до результата; «оплачено» уходит сама; «проблема» ждёт тапа. */
export function setPaymentStatus(orderId: string, phase: PaymentPhase): void {
  if (hideTimer) clearTimeout(hideTimer);
  hideTimer = null;
  const id = ++statusSeq;
  paymentStatusStore.set({ id, orderId, phase });
  if (phase === 'paid') {
    hideTimer = setTimeout(() => {
      if (paymentStatusStore.get()?.id === id) paymentStatusStore.set(null);
    }, 4000);
  } else if (phase === 'paying') {
    // Ответ так и не пришёл: «идёт оплата» не висит вечно, через 2 минуты (хватает на 3-D Secure с вводом кода) это уже проблема.
    hideTimer = setTimeout(() => {
      if (paymentStatusStore.get()?.id === id) setPaymentStatus(orderId, 'problem');
    }, 120_000);
  }
}

export function dismissPaymentStatus(): void {
  paymentStatusStore.set(null);
}

export async function loadPaymentConfig(): Promise<void> {
  try {
    paymentConfigStore.set(await payments.config());
  } catch {
    // Конфигурации нет: оплата идёт через страницу эквайера, остальное скрыто.
  }
}

export async function loadSavedCards(sync = false): Promise<void> {
  if (!paymentConfigStore.get().savedCards) {
    savedCardsStore.set({ items: [], loaded: true });
    return;
  }
  try {
    const { items } = await me.cards(sync);
    savedCardsStore.set({ items, loaded: true });
  } catch {
    savedCardsStore.set((prev) => ({ ...prev, loaded: true }));
  }
}

export function clearSavedCards(): void {
  savedCardsStore.set({ items: [], loaded: false });
}

export function defaultCard(): SavedCard | null {
  const { items } = savedCardsStore.get();
  return items.find((c) => c.isDefault) ?? items[0] ?? null;
}

/** Способ по умолчанию: карта клиента, иначе новая карта, иначе страница банка. */
export function defaultMethod(): PaymentMethod {
  const config = paymentConfigStore.get();
  if (config.cardEntry === 'inline') {
    const card = defaultCard();
    return card ? { kind: 'saved', cardId: card.id } : { kind: 'new' };
  }
  return config.hosted ? { kind: 'bank' } : { kind: 'test' };
}

export async function setDefaultCard(cardId: string): Promise<void> {
  await me.setDefaultCard(cardId);
  savedCardsStore.set((prev) => ({ ...prev, items: prev.items.map((c) => ({ ...c, isDefault: c.id === cardId })) }));
}

export async function deleteCard(cardId: string): Promise<void> {
  await me.deleteCard(cardId);
  await loadSavedCards();
}

/** Адрес возврата в веб-клиент после страницы подтверждения эквайера. */
export function paymentReturnUrl(orderId: string): string {
  return `${WEB_BASE_URL}/?order=${encodeURIComponent(orderId)}&payment=1`;
}

export type CardInput = { pan: string; expMonth: number; expYear: number; cvv: string; owner: string };

export type PayOutcome = 'redirect' | 'paid' | 'declined';

/**
 * Открыть страницу эквайера для заказа. `redirect` — браузер уходит; `paid` — оплата уже прошла;
 * `declined` — провайдер работает токеном и отказал.
 */
export async function goToPaymentPage(orderId: string): Promise<PayOutcome> {
  const result = await orders.pay(orderId, { returnTo: paymentReturnUrl(orderId), lang: currentLang() });
  if (result.status === 'redirect' && result.paymentUrl) {
    location.assign(result.paymentUrl);
    return 'redirect';
  }
  return result.status === 'paid' ? 'paid' : 'declined';
}

/** Холд по сохранённой карте: без ухода из приложения. */
export async function payWithSavedCard(orderId: string, cardId: string): Promise<{ status: 'paid' | 'declined'; reason: string | null }> {
  setPaymentStatus(orderId, 'paying');
  try {
    const result = await orders.pay(orderId, { savedCardId: cardId });
    if (result.status === 'paid') {
      setPaymentStatus(orderId, 'paid');
      return { status: 'paid', reason: null };
    }
    setPaymentStatus(orderId, 'problem');
    return { status: 'declined', reason: result.reason ?? null };
  } catch (error) {
    setPaymentStatus(orderId, 'problem');
    throw error;
  }
}

/**
 * Новая карта: сервер открывает сессию эквайера, а форма с номером карты уходит из браузера прямо
 * эквайеру (POST на его адрес, страница покидается). Дальше 3-D Secure банка и возврат.
 */
export async function payWithNewCard(orderId: string, card: CardInput): Promise<PayOutcome> {
  setPaymentStatus(orderId, 'paying');
  let result;
  try {
    result = await orders.pay(orderId, { newCard: true, returnTo: paymentReturnUrl(orderId), lang: currentLang() });
  } catch (error) {
    setPaymentStatus(orderId, 'problem');
    throw error;
  }
  if (result.status === 'paid') {
    setPaymentStatus(orderId, 'paid');
    return 'paid';
  }
  if (result.status !== 'card_entry' || !result.postUrl) {
    setPaymentStatus(orderId, 'problem');
    return 'declined';
  }
  const form = document.createElement('form');
  form.method = 'post';
  form.action = result.postUrl;
  form.style.display = 'none';
  const fields: Record<string, string> = {
    pan: card.pan.replace(/\D/g, ''),
    expiryMonth: String(card.expMonth).padStart(2, '0'),
    expiryYear: String(card.expYear),
    cvv: card.cvv,
    cardOwner: card.owner,
  };
  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.append(input);
  }
  document.body.append(form);
  form.submit();
  return 'redirect';
}

const CARD_VERIFICATION_KEY = 'trotty10.cardVerification';

/**
 * Привязка карты без заказа: сервер открывает сессию эквайера на проверочный холд 1 RSD, форма с
 * номером карты уходит из браузера прямо эквайеру (страница покидается). Дальше 3-D Secure банка
 * и возврат на `/?card=1`; id попытки до ухода запоминается в localStorage.
 */
export async function saveNewCard(card: CardInput): Promise<void> {
  const started = await me.verifyCard({ returnTo: `${WEB_BASE_URL}/?card=1`, lang: currentLang() });
  localStorage.setItem(CARD_VERIFICATION_KEY, started.verificationId);
  postCardToAcquirer(started.postUrl, card);
}

/** Попытка привязки, с которой браузер ушёл к эквайеру; снимается при чтении. */
export function takePendingCardVerification(): string | null {
  const id = localStorage.getItem(CARD_VERIFICATION_KEY);
  localStorage.removeItem(CARD_VERIFICATION_KEY);
  return id;
}

function postCardToAcquirer(postUrl: string, card: CardInput): void {
  const form = document.createElement('form');
  form.method = 'post';
  form.action = postUrl;
  form.style.display = 'none';
  const fields: Record<string, string> = {
    pan: card.pan.replace(/\D/g, ''),
    expiryMonth: String(card.expMonth).padStart(2, '0'),
    expiryYear: String(card.expYear),
    cvv: card.cvv,
    cardOwner: card.owner,
  };
  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.append(input);
  }
  document.body.append(form);
  form.submit();
}

/** Результат привязки после возврата: эквайер может ответить не сразу, поэтому несколько запросов подряд. */
export async function confirmCardAfterReturn(verificationId: string): Promise<CardVerificationResult> {
  let last: CardVerificationResult = { status: 'pending' };
  for (let attempt = 0; attempt < CONFIRM_ATTEMPTS; attempt++) {
    last = await me.confirmCardVerification(verificationId);
    if (last.status !== 'pending') break;
    await new Promise((resolve) => setTimeout(resolve, CONFIRM_INTERVAL_MS));
  }
  await loadSavedCards(true);
  return last;
}

const CONFIRM_ATTEMPTS = 8;
const CONFIRM_INTERVAL_MS = 2500;

/** Результат после возврата: эквайер может ответить не сразу, поэтому несколько запросов подряд. */
export async function confirmPaymentAfterReturn(orderId: string): Promise<PaymentConfirmation> {
  setPaymentStatus(orderId, 'paying');
  let last: PaymentConfirmation = { status: 'pending' };
  try {
    for (let attempt = 0; attempt < CONFIRM_ATTEMPTS; attempt++) {
      last = await orders.confirmPayment(orderId);
      if (last.status !== 'pending') break;
      await new Promise((resolve) => setTimeout(resolve, CONFIRM_INTERVAL_MS));
    }
  } catch (error) {
    setPaymentStatus(orderId, 'problem');
    throw error;
  }
  setPaymentStatus(orderId, last.status === 'paid' ? 'paid' : 'problem');
  if (last.status === 'paid') void loadSavedCards(true);
  return last;
}
